// Admin-side QR-sharing modal for re-order links (G4.3). Lets a MANAGER+ user
// generate a link for any bill in the tenant and hand it to the customer as a
// QR code, copyable URL, or WhatsApp/Email deep-link.

import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { Button, Input, Select, GLASS, cn } from '@bbpos/ui';
import { communicationsApi } from '@/features/communications/api';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { useTenantDial } from '@/hooks/use-tenant-dial';
import { ApiError } from '@/lib/api-client';

type Channel = 'WHATSAPP' | 'EMAIL';

export interface ReorderShareModalProps {
  defaultBillId?: string;
  onClose: () => void;
  onCreated?: () => void;
}

function isValidUuid(v: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
}
function isValidPhone(v: string): boolean {
  return /^\+[1-9]\d{6,14}$/.test(v);
}
function isValidEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

function addQrSrc(link: string): string {
  const sep = link.includes('?') ? '&' : '?';
  return `${link}${sep}src=qr`;
}

export function ReorderShareModal({ defaultBillId, onClose, onCreated }: ReorderShareModalProps) {
  const dial = useTenantDial();
  const currency = useTenantCurrency();
  const [billId, setBillId] = useState(defaultBillId ?? '');

  // Searchable bill picker — Owners/Managers never know raw bill UUIDs, so we
  // surface recent completed bills by their human-readable bill number.
  const { data: recentBills } = useQuery({
    queryKey: ['reorder-share-bills'],
    queryFn: () => communicationsApi.recentBills(),
    staleTime: 60_000,
  });
  const billOptions = useMemo(
    () =>
      (recentBills ?? []).map((b) => ({
        value: b.id,
        label: `${b.bill_number} — ${formatPaise(b.grand_total + (b.rounding_adjustment ?? 0), currency)}`,
      })),
    [recentBills, currency],
  );

  const [channel, setChannel] = useState<Channel>('WHATSAPP');
  const [recipient, setRecipient] = useState('');
  const [qr, setQr] = useState<{ link: string; dataUrl: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canSubmit =
    isValidUuid(billId)
    && (channel === 'WHATSAPP' ? isValidPhone(recipient) : isValidEmail(recipient));

  const generate = useCallback(async () => {
    if (!canSubmit) return;
    setError(null);
    setIsSubmitting(true);
    try {
      const result = await communicationsApi.createReorderLink({
        bill_id: billId,
        channel,
        recipient,
      });
      const link = addQrSrc(result.reorder_link);
      const dataUrl = await QRCode.toDataURL(link, { width: 256, margin: 1 });
      setQr({ link, dataUrl });
      onCreated?.();
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to generate re-order link';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [billId, canSubmit, channel, recipient, onCreated]);

  const copyLink = useCallback(async () => {
    if (!qr) return;
    try {
      await navigator.clipboard.writeText(qr.link);
    } catch {
      /* ignore */
    }
  }, [qr]);

  const whatsappHref = qr && channel === 'WHATSAPP'
    ? `https://wa.me/${recipient.replace(/\D/g, '')}?text=${encodeURIComponent(`Re-order your last purchase: ${qr.link}`)}`
    : null;
  const emailHref = qr && channel === 'EMAIL'
    ? `mailto:${recipient}?subject=${encodeURIComponent('Re-order your last purchase')}&body=${encodeURIComponent(`Tap to re-order: ${qr.link}`)}`
    : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Share re-order QR"
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/60 backdrop-blur-sm p-4 animate-fade-in"
      onClick={onClose}
    >
      <div
        className={cn(GLASS, 'max-w-md w-full p-6 space-y-4 animate-scale-in')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">Share re-order link</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800"
            aria-label="Close"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {!qr && (
          <>
            <Select
              label="Bill"
              variant="searchable"
              options={billOptions}
              value={billId}
              onChange={(v) => setBillId(typeof v === 'string' ? v : '')}
              placeholder="Search by bill number…"
              required
            />

            <div role="tablist" className="flex gap-2 rounded-xl bg-neutral-100 dark:bg-neutral-800 p-1">
              {(['WHATSAPP', 'EMAIL'] as Channel[]).map((c) => (
                <button
                  key={c}
                  role="tab"
                  aria-selected={channel === c}
                  type="button"
                  onClick={() => { setChannel(c); setRecipient(''); setError(null); }}
                  className={cn(
                    'flex-1 rounded-lg px-3 py-1.5 text-sm font-medium transition-all',
                    channel === c
                      ? 'bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 shadow-sm'
                      : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700',
                  )}
                >
                  {c === 'WHATSAPP' ? 'WhatsApp' : 'Email'}
                </button>
              ))}
            </div>

            {channel === 'WHATSAPP' ? (
              <Input
                variant="phone"
                defaultDial={dial}
                label="WhatsApp number"
                placeholder="98765 43210"
                value={recipient}
                onChange={setRecipient}
                error={error ?? undefined}
              />
            ) : (
              <Input
                type="email"
                label="Email address"
                placeholder="customer@example.com"
                value={recipient}
                onChange={setRecipient}
                error={error ?? undefined}
              />
            )}

            <Button
              onClick={generate}
              disabled={!canSubmit || isSubmitting}
              loading={isSubmitting}
              variant="primary"
              className="w-full"
            >
              Generate & send
            </Button>
          </>
        )}

        {qr && (
          <div className="space-y-4 animate-fade-in">
            <div className="flex justify-center">
              <div className="rounded-2xl bg-white p-3 shadow-lg">
                <img src={qr.dataUrl} alt="Re-order QR code" width={224} height={224} />
              </div>
            </div>
            <p className="text-center text-xs text-neutral-500 dark:text-neutral-400">
              Link sent via {channel === 'WHATSAPP' ? 'WhatsApp' : 'Email'} and ready to scan.
            </p>
            <div className="flex gap-2">
              <Button onClick={copyLink} variant="secondary" className="flex-1">Copy link</Button>
              {whatsappHref && (
                <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="flex-1">
                  <Button variant="primary" className="w-full">Open WhatsApp</Button>
                </a>
              )}
              {emailHref && (
                <a href={emailHref} className="flex-1">
                  <Button variant="primary" className="w-full">Open Email</Button>
                </a>
              )}
            </div>
            <Button variant="ghost" className="w-full" onClick={onClose}>Done</Button>
          </div>
        )}
      </div>
    </div>
  );
}
